/**
 * hooks/useCrmPermissions.ts
 *
 * Single source of truth for CRM role-based access.
 *
 * Rules (per ticket "CRM Role-Based Record Access, Ownership & Assignment Permissions"):
 *  - CRM_ADMIN / CRM_MANAGER (plus ORG_OWNER / ORG_ADMIN) -> complete access, never
 *    restricted by ownership or Task/Note assignment (sections 2, 3, 22).
 *  - CRM_USER -> view everything in the org; edit only where the server says so.
 *    The server's `canEdit` encodes "is Owner OR has an assigned Task OR has an
 *    assigned Note" (sections 5, 25). The frontend cannot compute this on its own —
 *    detail payloads do not carry the full Task/Note assignee lists — so `canEdit`
 *    is authoritative and must not be second-guessed here.
 *  - Ownership is never granted by assignment (section 7), and reassigning an Owner
 *    stays an Admin/Manager capability (sections 2, 3).
 */
import { useCallback, useMemo } from 'react';
import { useAppSelector } from 'store';
// NOTE: adjust to whatever your auth slice exports. `selectAuthUser` must return the
// object containing `appUserUuid`.
import { selectRoles, selectAuthUser } from 'store/slices/auth/auth.selector';

/** Roles with unconditional CRM access. */
export const CRM_FULL_ACCESS_ROLES = [
    'ORG_OWNER',
    'ORG_ADMIN',
    'CRM_ADMIN',
    'CRM_MANAGER',
] as const;

/**
 * Who may write to the org-wide tag library (CRM_Tags_API.md, "Who can do what").
 *
 * Deliberately NOT `CRM_FULL_ACCESS_ROLES`: the tags API grants writes to
 * CRM_ADMIN but not CRM_MANAGER, so reusing full access would show a Manager
 * buttons the server answers with 403.
 */
export const CRM_TAG_ADMIN_ROLES = [
    'ORG_OWNER',
    'ORG_ADMIN',
    'CRM_ADMIN',
] as const;

/**
 * Minimum shape every access-controlled CRM record must expose.
 * Contacts, Companies and Deals all satisfy this today.
 */
export interface AccessControlledRecord {
    /** Server-computed: Owner OR assigned Task OR assigned Note. Authoritative. */
    canEdit?: boolean | null;
    owner?: { uuid?: string | null; name?: string | null } | null;
}

export const useCrmPermissions = () => {
    const roles = useAppSelector(selectRoles);
    const authUser = useAppSelector(selectAuthUser);

    const currentUserUuid: string | null = authUser?.appUserUuid ?? null;

    const hasFullAccess = useMemo(
        () =>
            (roles ?? []).some((role: string) =>
                (CRM_FULL_ACCESS_ROLES as readonly string[]).includes(role),
            ),
        [roles],
    );

    /**
     * Ownership check only. Deliberately NOT the access rule — a CRM User can hold
     * edit access without being Owner (Task/Note assignment), and being Owner of a
     * Contact says nothing about the parent Company (section 20/21). Exposed for UI
     * that genuinely needs to say "you own this".
     */
    const isOwner = useCallback(
        (record?: AccessControlledRecord | null) =>
            !!record && !!currentUserUuid && record.owner?.uuid === currentUserUuid,
        [currentUserUuid],
    );

    const canEditRecord = useCallback(
        (record?: AccessControlledRecord | null): boolean => {
            if (hasFullAccess) return true;
            if (!record) return false;

            if (typeof record.canEdit === 'boolean') return record.canEdit;

            // Fallback for endpoints that don't return the flag yet (e.g. some list
            // responses). Ownership-only, so it fails closed for Task/Note-granted
            // access rather than opening up a record the server would refuse.
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn(
                    '[useCrmPermissions] Record is missing `canEdit`; falling back to owner match. ' +
                    'Ask the API to include canEdit on this payload.',
                );
            }
            return isOwner(record);
        },
        [hasFullAccess, isOwner],
    );

    /**
     * Creating / editing / deactivating / deleting tags in Settings → Tags.
     * Reading the library (and the assignable list) is open to every CRM user.
     */
    const canManageTags = useMemo(
        () =>
            (roles ?? []).some((role: string) =>
                (CRM_TAG_ADMIN_ROLES as readonly string[]).includes(role),
            ),
        [roles],
    );

    return {
        roles,
        currentUserUuid,
        hasFullAccess,
        canManageTags,
        isOwner,
        canEditRecord,
        /** Section 6: a CRM User with access may delete. Same rule as edit. */
        canDeleteRecord: canEditRecord,
        /** Sections 2, 3: ownership management is Admin/Manager only. */
        canReassignOwner: hasFullAccess,
        /** Section 24: "Add Users" is Admin/Manager only. */
        canAddUsers: hasFullAccess,
        /** Bulk contact import is not offered to CRM_USER. */
        canImportContacts: hasFullAccess,
        /**
         * Creating a brand-new Contact / Company / Deal.
         *
         * Open to every CRM role by product decision: creation is not record-scoped
         * (there is no Owner or assignment to evaluate yet) and the creator becomes
         * Owner, which grants edit access anyway. The ticket lists "Create" only under
         * Admin/Manager (sections 2, 3) and omits it from the CRM User matrix
         * (section 6) — read as "not applicable" rather than "denied".
         *
         * If that gets overruled, change this to `hasFullAccess` — the "Add Contact /
         * Company / Deal" buttons and empty-state CTAs are the only consumers.
         */
        canCreateRecords: true,
    };
};

export default useCrmPermissions;